import {
  memo,
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

import styled from '@mint-lib/styled';

import { IconButton, Typography } from '@myn/mui';

type Props = {
  title: string;
  children: ReactNode;
  panelRef?: MutableRefObject<HTMLDivElement | null>;
  isActive?: boolean;
  onClose?: () => void;
  className?: string;
};

const DrawerTabPanel = ({
  title,
  className,
  panelRef,
  isActive = false,
  onClose = () => null,
  children,
}: Props) => {
  const [opacity, setOpacity] = useState<number>(0);

  useEffect(() => {
    if (!isActive) {
      setOpacity(0);
      return;
    }

    setTimeout(() => {
      setOpacity(1);
    }, 300);
  }, [isActive]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isActive) {
    return null;
  }

  return (
    <Wrapper
      className={className}
      ref={panelRef}
      $isActive={isActive}
      $opacity={opacity}
    >
      <HeaderWrapper>
        <IconButton
          icon="Close"
          color="inherit"
          size="small"
          variant="icon"
          onClick={handleClose}
        />
        <Typography variant="h3">{title}</Typography>
      </HeaderWrapper>
      <ContentWrapper>{children}</ContentWrapper>
    </Wrapper>
  );
};

const Wrapper = styled('div')<{ $isActive?: boolean; $opacity?: number }>`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing(3)};
  height: 100%;
  width: max-content;
  background-color: ${({ theme }) => theme.palette.ui.primary};
  opacity: ${({ $opacity }) => $opacity};
  transition: opacity ease 0.3s;
  overflow: hidden;
`;

const HeaderWrapper = styled('div')`
  display: flex;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(1)};
  margin-left: ${({ theme }) => theme.spacing(-3)};
  padding: ${({ theme }) =>
    `${theme.spacing(3)} ${theme.spacing(3)} ${theme.spacing(2)} ${theme.spacing(3)}`};
  background-color: none;
  color: ${({ theme }) => theme.palette.text.primary};
`;

const ContentWrapper = styled('div')`
  width: 100%;
  height: 100%;
`;

export default memo(DrawerTabPanel);
